/**
 * Audit log service — calls hub proxy for `/v1/cms/audit-log`.
 * Returns AuditEntry rows with actionKey + args used by the timeline.
 */
import type { AuditEntry, AuditListFilter, AuditListPage } from "@/types/audit-log.types";
import { hubCall } from "@/lib/api/hub";

// Wire shape returned by GET /v1/cms/audit-log: `datatype.ResponseDataWithPaging`
// = `{success, data:[...AuditLogDTO], paging:{page,limit,total}}`. The timeline
// consumer (`AuditListPage`) wants `{items, nextPage, total}`, so we adapt at
// this boundary instead of leaking BE shape into TanStack Query.
interface AuditListBackendResponse {
  success: boolean;
  data: AuditEntry[];
  paging?: { page: number; limit: number; total: number };
}

function toRFC3339(localDt: string | undefined): string | undefined {
  if (!localDt) return undefined;
  const ms = Date.parse(localDt);
  if (Number.isNaN(ms)) return undefined;
  return new Date(ms).toISOString();
}

function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      // multi-value params: ?actionKey[]=a&actionKey[]=b
      for (const v of value) {
        if (v !== undefined && v !== null && v !== "") {
          searchParams.append(`${key}[]`, String(v));
        }
      }
    } else {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

export const auditService = {
  /** Structured list for the timeline — returns AuditEntry with actionKey + args. */
  async listEntries(filter: AuditListFilter = {}): Promise<AuditListPage> {
    const params: Record<string, unknown> = {
      actor: filter.actor,
      from: toRFC3339(filter.from),
      to: toRFC3339(filter.to),
      page: filter.page,
      limit: filter.limit ?? 50,
    };
    if (filter.actionKey?.length) params["actionKey"] = filter.actionKey;
    if (filter.source?.length) params["source"] = filter.source;
    if (filter.resourceType?.length) params["resourceType"] = filter.resourceType;
    if (filter.resourceId) params.resourceId = filter.resourceId;

    const res = await hubCall<AuditListBackendResponse>(
      `/audit-log${buildQueryString(params)}`,
    );
    const items = res.data ?? [];
    const paging = res.paging;
    // nextPage = current+1 when there are more rows than (page * limit), else null.
    const limit = paging?.limit ?? params.limit as number;
    const page = paging?.page ?? (params.page as number) ?? 1;
    const total = paging?.total ?? items.length;
    const hasMore = items.length === limit && page * limit < total;
    return {
      items,
      total,
      nextPage: hasMore ? page + 1 : null,
    };
  },

  /** Resource-scoped redacted audit rows for entity Activity surfaces. */
  async listResourceEntries(filter: AuditListFilter & { resourceType: string[]; resourceId: string }): Promise<AuditListPage> {
    const resourceType = filter.resourceType[0];
    const params: Record<string, unknown> = {
      resourceType,
      resourceId: filter.resourceId,
      actor: filter.actor,
      from: toRFC3339(filter.from),
      to: toRFC3339(filter.to),
      page: filter.page,
      limit: filter.limit ?? 50,
    };
    if (filter.actionKey?.length) params["actionKey"] = filter.actionKey;
    if (filter.source?.length) params["source"] = filter.source;

    const res = await hubCall<AuditListBackendResponse>(
      `/resource-activity/audit${buildQueryString(params)}`,
    );
    const items = res.data ?? [];
    const paging = res.paging;
    const limit = paging?.limit ?? params.limit as number;
    const page = paging?.page ?? (params.page as number) ?? 1;
    const total = paging?.total ?? items.length;
    const hasMore = items.length === limit && page * limit < total;
    return {
      items,
      total,
      nextPage: hasMore ? page + 1 : null,
    };
  },

};
